import { computed, watchEffect } from 'vue';

import { DataCache } from '@/shared/data/cache';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

/**
 * Whether the tenant runs the modern (zynotalk) voice backend, or `undefined` while that is still
 * unknown. Backed by the `talkTenant` cache entry, so GET /tenant is fetched once per tenant and
 * re-fetched automatically on a tenant switch, and the admin section nav, the Talk landing, admin
 * search and the Talk screens can never disagree about it.
 *
 * `undefined` means "not known yet" — it must NOT be read as legacy. A screen whose layout depends
 * on the backend should hold its render until this settles rather than draw a legacy layout that
 * flips a moment later; a nav predicate that only hides an item can compare against `true`.
 *
 * Reading this does not trigger a fetch. Call `useIsModernTalkBackend()` from the setup of any
 * screen or modal that needs it.
 */
export const isModernTalkBackend = computed<boolean | undefined>(() => {
    const tenant = DataCache.getLazyRef('talkTenant').value;
    return tenant === undefined ? undefined : tenant?.voiceBackend === 'zynotalk';
});

/** Returns the shared flag, kicking the (tenant-scoped, deduped) load if it hasn't run yet. */
export function useIsModernTalkBackend() {
    DataCache.getRef('talkTenant');
    return isModernTalkBackend;
}

/**
 * Whether Talk's voice features may be offered to the signed-in user at all: they hold Talk access, and
 * the tenant runs the backend those features live on. This is the gate for a *non-Talk* surface that
 * wants to offer one of them — ZynoForms' voice capture, say — since neither half of the answer is
 * knowable from inside that surface.
 *
 * False while the backend is still settling, so a feature arrives late rather than appearing and then
 * vanishing (the same call `AdminTalkNav`'s `modernOnly` makes).
 */
export const canUseTalkVoiceFeatures = computed<boolean>(() => hasPermission('talk') && isModernTalkBackend.value === true);

/**
 * Returns the shared flag, kicking the tenant load — but only for a user who could act on the answer, so
 * a Forms-only user makes no Talk request at all. Call from the setup of any screen or modal that offers
 * a voice feature.
 *
 * `watchEffect` rather than a bare check (the shape `useCallerBlocking` uses): permissions are a ref, and
 * a component that mounts before they settle would otherwise never kick the load and hide the feature
 * for good.
 */
export function useCanUseTalkVoiceFeatures() {
    watchEffect(() => {
        if (hasPermission('talk')) DataCache.getRef('talkTenant');
    });
    return canUseTalkVoiceFeatures;
}
